/**
 * Hook que gerencia a atualização de fontes do sistema
 * Provê funções para verificar, baixar e atualizar fontes do cache
 */
import { createRoot } from 'react-dom/client';
import { AvisoAtualizacao } from '../components/AvisoAtualizacao';

/**
 * Interface para as opções do hook
 */
interface UseAtualizacaoOptions {
  // Função opcional para limpar o cache de fontes em memória
  clearFontCache?: () => void;
}

export function useAtualizacao(options: UseAtualizacaoOptions = {}) {
  // Nome do cache usado para armazenar as fontes
  const CACHE_NAME = 'font-cache';
  // Nome do arquivo JSON que contém informações sobre as fontes
  const FONTS_JSON = 'fontes.json';
  
  // Armazena os dados das fontes obtidos do banco para evitar múltiplas requisições
  let fontesDBCache: FonteDB[] | null = null;
  let ultimaAtualizacaoFontesDB = 0;
  // Intervalo de cache em ms (5 minutos)
  const CACHE_TIMEOUT = 5 * 60 * 1000;

  /**
   * Normaliza o nome da fonte para comparações
   * @param nomeFonte Nome da fonte para normalizar
   * @returns Nome normalizado (minúsculas e sem espaços extras)
   */
  const normalizarNomeFonte = (nomeFonte: string): string => {
    return nomeFonte.toLowerCase().trim();
  };

  /**
   * Procura ou cria o arquivo fontes.json no cache
   * Este arquivo mantém registro de todas as fontes armazenadas
   * @returns Objeto com dados das fontes ou null em caso de erro
   */
  const procurarFontesJson = async () => {
    try {
      // Verifica se a API Cache está disponível
      if (!('caches' in window)) {
        console.error('[procurarFontesJson] API Cache não disponível neste navegador');
        return null;
      }
      
      // Abre o cache de fontes
      const fontCache = await caches.open(CACHE_NAME);
      const fontesJsonResponse = await fontCache.match(`/${FONTS_JSON}`);
      
      if (fontesJsonResponse) {
        // Arquivo já existe, vamos ler e retornar
        const fontesJson = await fontesJsonResponse.json();
        return fontesJson;
      }
      
      // Arquivo não existe, vamos criar
      
      // Listar todas as fontes disponíveis no cache
      const cachedRequests = await fontCache.keys();
      const fontesRequests = cachedRequests.filter(request => {
        const url = new URL(request.url);
        return url.pathname.toLowerCase().includes('/fonts/') && 
               url.pathname.toLowerCase().endsWith('.ttf');
      });
      
      // Obter as datas dos cabeçalhos das respostas
      const fontesInfo = await Promise.all(fontesRequests.map(async (request) => {
        const response = await fontCache.match(request);
        const url = new URL(request.url);
        // Extrair o nome do arquivo do path
        const fontName = url.pathname.split('/').pop() || '';
        
        // CORREÇÃO: Priorizar a data do banco (data-db) para evitar loops com hora atrasada
        let dataAtualizacao = response?.headers.get('data-db');
        
        if (!dataAtualizacao) {
          // Fallback: tentar usar data do header padrão ou data atual
          if (response && response.headers.get('date')) {
            try {
              dataAtualizacao = new Date(response.headers.get('date') || '').toISOString();
            } catch (e) {
              // Se houver erro ao converter a data do cabeçalho, usa data atual
              dataAtualizacao = new Date().toISOString();
            }
          } else {
            // Se não tem nenhum header, usa data atual
            dataAtualizacao = new Date().toISOString();
          }
        } else {
          // Garantir que a data do banco está em formato ISO válido
          try {
            dataAtualizacao = new Date(dataAtualizacao).toISOString();
          } catch (e) {
            // Se houver erro na conversão, usar data atual como fallback
            dataAtualizacao = new Date().toISOString();
          }
        }
        
        return {
          nome: fontName,
          dataAtualizacao: dataAtualizacao
        };
      }));
      
      // Filtrar para remover possíveis duplicatas usando a função de normalização
      const fontesUnicas = new Map();
      for (const fonte of fontesInfo) {
        // Normaliza o nome para usar como chave
        const nomeNormalizado = normalizarNomeFonte(fonte.nome);
        // Armazena utilizando o nome normalizado como chave
        fontesUnicas.set(nomeNormalizado, fonte);
      }
      
      // Criar estrutura de dados para o arquivo fontes.json
      const dataAtual = new Date().toISOString();
      const fontesData = {
        fontes: Array.from(fontesUnicas.values()),
        ultimaVerificacao: dataAtual
      };
      
      // Salvar o arquivo no cache
      const fontesJsonBlob = new Blob([JSON.stringify(fontesData, null, 2)], {
        type: 'application/json'
      });
      
      await fontCache.put(
        `/${FONTS_JSON}`,
        new Response(fontesJsonBlob, {
          headers: {
            'Content-Type': 'application/json',
            'date': new Date().toUTCString()
          }
        })
      );
      
      return fontesData;
    } catch (error) {
      console.error('[procurarFontesJson] Erro ao processar fontes.json:', error);
      return null;
    }
  };  

  /**
   * Interface para representar uma fonte do banco de dados
   */
  interface FonteDB {
    id?: string;
    arquivo: string;
    data_atualizacao: string;
  }

  /**
   * Interface para representar o resultado da comparação entre fontes
   */
  interface ResultadoComparacao {
    fontesParaAtualizar: FonteDB[]; // Fontes que existem no cache mas estão desatualizadas
    fontesParaBaixar: FonteDB[];    // Fontes que não existem no cache
    fontesAtualizadas: FonteDB[];   // Fontes que já estão atualizadas
  }

  /**
   * Busca fontes armazenadas no banco de dados Supabase
   * Esta função verifica primeiro se há dados em cache e só faz a requisição se necessário
   * @param forcarAtualizacao Se true, ignora o cache e faz uma nova requisição
   * @returns Array de fontes ou null em caso de erro
   */
  const buscarFontesDatabase = async (forcarAtualizacao = false): Promise<FonteDB[] | null> => {
    try {
      // Verificar se temos dados em cache válidos
      const agora = Date.now();
      if (!forcarAtualizacao && fontesDBCache && (agora - ultimaAtualizacaoFontesDB < CACHE_TIMEOUT)) {
        return fontesDBCache;
      }
      
      // Importar supabase
      const { supabase } = await import('../lib/supabase');
      
      // Buscar fontes do banco de dados
      const { data, error } = await supabase
        .from('fontes')
        .select('arquivo, data_atualizacao')
        .order('data_atualizacao', { ascending: false });
      
      if (error) {
        console.error('[buscarFontesDatabase] Erro ao buscar fontes do banco:', error);
        return null;
      }
      
      if (!data || data.length === 0) {
        fontesDBCache = [];
        ultimaAtualizacaoFontesDB = agora;
        return [];
      }
      
      // Agrupa por arquivo e mantém apenas a entrada mais recente
      const fontesUnicas = new Map<string, FonteDB>();

      data.forEach((fonte: FonteDB) => {
        // Ignora registros sem arquivo: nunca casam com o cache e resultariam em
        // um download de "fonts/" (400), tentado indefinidamente a cada sessão
        if (!fonte.arquivo?.trim()) {
          console.warn('[buscarFontesDatabase] Registro de fonte sem arquivo ignorado:', fonte);
          return;
        }

        if (!fontesUnicas.has(fonte.arquivo)) {
          fontesUnicas.set(fonte.arquivo, fonte);
        }
      });
      
      // Converte o Map para array e ordena por arquivo
      const fontesArray = Array.from(fontesUnicas.values())
        .sort((a, b) => a.arquivo.localeCompare(b.arquivo));
      
      // Armazenar no cache
      fontesDBCache = fontesArray;
      ultimaAtualizacaoFontesDB = agora;
      
      return fontesArray;
    } catch (error) {
      console.error('[buscarFontesDatabase] Erro inesperado:', error);
      return null;
    }
  };

  /**
   * Limpa o cache de fontes do banco de dados
   * Útil quando sabemos que os dados podem ter mudado
   */
  const limparCacheFontesDB = () => {
    fontesDBCache = null;
    ultimaAtualizacaoFontesDB = 0;
  };

  /**
   * Compara fontes do cache com fontes do banco de dados
   * Identifica quais fontes precisam ser baixadas ou atualizadas
   * @returns Objeto com o resultado da comparação ou null em caso de erro
   */
  const compararFontes = async (): Promise<ResultadoComparacao | null> => {
    try {
      // Buscar fontes do banco de dados (usa cache se disponível)
      const fontesDB = await buscarFontesDatabase();
      if (!fontesDB) {
        console.error('[compararFontes] Não foi possível obter fontes do banco de dados');
        return null;
      }
      
      // Buscar fontes do cache
      const fontesCache = await procurarFontesJson();
      if (!fontesCache) {
        console.error('[compararFontes] Não foi possível obter fontes do cache');
        return null;
      }
      
      // Criar um mapa com as fontes do cache para facilitar a comparação
      const mapaCacheNormalizado = new Map<string, { nome: string, dataAtualizacao: string }>();
      
      fontesCache.fontes.forEach((fonte: { nome: string, dataAtualizacao: string }) => {
        const nomeNormalizado = normalizarNomeFonte(fonte.nome);
        mapaCacheNormalizado.set(nomeNormalizado, fonte);
      });
      
      // Separar as fontes em categorias
      const fontesParaAtualizar: FonteDB[] = [];
      const fontesParaBaixar: FonteDB[] = [];
      const fontesAtualizadas: FonteDB[] = [];
      
      fontesDB.forEach(fonteDB => {
        // Normalizar o nome do arquivo para comparação
        const nomeNormalizado = normalizarNomeFonte(fonteDB.arquivo);
        const fonteCache = mapaCacheNormalizado.get(nomeNormalizado);
        
        if (!fonteCache) {
          // Fonte não existe no cache, precisa ser baixada
          fontesParaBaixar.push(fonteDB);
        } else {
          // Comparar datas para verificar se está atualizada
          const dataCache = new Date(fonteCache.dataAtualizacao);
          const dataDB = new Date(fonteDB.data_atualizacao);
          
          if (dataDB > dataCache) {
            // Fonte do banco é mais recente, precisa atualizar
            fontesParaAtualizar.push(fonteDB);
          } else {
            // Fonte já está atualizada
            fontesAtualizadas.push(fonteDB);
          }
        }
      });
      
      const resultado: ResultadoComparacao = {
        fontesParaAtualizar,
        fontesParaBaixar,
        fontesAtualizadas
      };
      
      return resultado;
    } catch (error) {
      console.error('[compararFontes] Erro ao comparar fontes:', error);
      return null;
    }
  };

  /**
   * Gera um relatório detalhado das fontes que precisam ser atualizadas
   * Útil para diagnóstico e apresentação do estado das fontes
   * @returns String com o relatório formatado ou null em caso de erro
   */
  const gerarRelatorioAtualizacao = async (): Promise<string | null> => {
    try {
      // Obter o resultado da comparação
      const resultadoComparacao = await compararFontes();
      if (!resultadoComparacao) {
        return null;
      }
      
      // Obter as fontes do cache para referência
      const fontesCache = await procurarFontesJson();
      if (!fontesCache) {
        return null;
      }
      
      // Criar mapa de fontes do cache
      const mapaCacheNormalizado = new Map<string, { nome: string, dataAtualizacao: string }>();
      fontesCache.fontes.forEach((fonte: { nome: string, dataAtualizacao: string }) => {
        const nomeNormalizado = normalizarNomeFonte(fonte.nome);
        mapaCacheNormalizado.set(nomeNormalizado, fonte);
      });
      
      // Gerar linhas do relatório
      let relatorio = `RELATÓRIO DE ATUALIZAÇÕES DE FONTES\n`;
      relatorio += `Data da verificação: ${new Date().toLocaleString()}\n\n`;
      
      // 1. Fontes para atualizar
      relatorio += `FONTES PARA ATUALIZAR (${resultadoComparacao.fontesParaAtualizar.length}):\n`;
      if (resultadoComparacao.fontesParaAtualizar.length === 0) {
        relatorio += `- Nenhuma fonte precisa ser atualizada\n`;
      } else {
        resultadoComparacao.fontesParaAtualizar.forEach(fonte => {
          const nomeNormalizado = normalizarNomeFonte(fonte.arquivo);
          const fonteCache = mapaCacheNormalizado.get(nomeNormalizado);
          
          if (fonteCache) {
            const dataCache = new Date(fonteCache.dataAtualizacao);
            const dataDB = new Date(fonte.data_atualizacao);
            
            relatorio += `- ${fonte.arquivo}\n`;
            relatorio += `  • Data no cache: ${dataCache.toLocaleString()}\n`;
            relatorio += `  • Data no banco: ${dataDB.toLocaleString()}\n`;
            relatorio += `  • Diferença: ${Math.floor((dataDB.getTime() - dataCache.getTime()) / (1000 * 60 * 60 * 24))} dias\n`;
          }
        });
      }
      
      // 2. Fontes para baixar
      relatorio += `\nFONTES PARA BAIXAR (${resultadoComparacao.fontesParaBaixar.length}):\n`;
      if (resultadoComparacao.fontesParaBaixar.length === 0) {
        relatorio += `- Todas as fontes já estão no cache\n`;
      } else {
        resultadoComparacao.fontesParaBaixar.forEach(fonte => {
          const dataDB = new Date(fonte.data_atualizacao);
          relatorio += `- ${fonte.arquivo} (Data no banco: ${dataDB.toLocaleString()})\n`;
        });
      }
      
      // 3. Fontes atualizadas
      relatorio += `\nFONTES JÁ ATUALIZADAS (${resultadoComparacao.fontesAtualizadas.length}):\n`;
      if (resultadoComparacao.fontesAtualizadas.length === 0) {
        relatorio += `- Nenhuma fonte está atualizada\n`;
      } else {
        relatorio += `- ${resultadoComparacao.fontesAtualizadas.length} fontes já estão atualizadas\n`;
      }
      
      // Resumo
      relatorio += `\nRESUMO:\n`;
      relatorio += `- Total de fontes no banco: ${resultadoComparacao.fontesParaAtualizar.length + resultadoComparacao.fontesParaBaixar.length + resultadoComparacao.fontesAtualizadas.length}\n`;
      relatorio += `- Total de fontes no cache: ${fontesCache.fontes.length}\n`;
      relatorio += `- Fontes que precisam ser atualizadas: ${resultadoComparacao.fontesParaAtualizar.length}\n`;
      relatorio += `- Fontes que precisam ser baixadas: ${resultadoComparacao.fontesParaBaixar.length}\n`;
      relatorio += `- Fontes já atualizadas: ${resultadoComparacao.fontesAtualizadas.length}\n`;
      
      return relatorio;
    } catch (error) {
      console.error('[gerarRelatorioAtualizacao] Erro ao gerar relatório:', error);
      return null;
    }
  };

  /**
   * Baixa e atualiza as fontes pendentes
   * Exibe um modal de progresso durante o processo
   * @returns true se pelo menos uma fonte foi atualizada com sucesso, false caso contrário
   */
  const atualizarFontes = async (): Promise<boolean> => {
    try {
      // Limpar o cache de fontes para forçar uma nova consulta
      limparCacheFontesDB();
      
      // Verifica quais fontes precisam ser baixadas ou atualizadas
      const resultadoComparacao = await compararFontes();
      if (!resultadoComparacao) {
        console.error('[atualizarFontes] Erro ao comparar fontes');
        return false;
      }
      
      // Combina as fontes que precisam ser baixadas e atualizadas
      const fontesPendentes = [
        ...resultadoComparacao.fontesParaBaixar,
        ...resultadoComparacao.fontesParaAtualizar
      ];
      
      if (fontesPendentes.length === 0) {
        return true;
      }
      
      // Criar elemento para o modal React
      const modalRoot = document.createElement('div');
      modalRoot.id = 'fonte-atualizacao-root';
      document.body.appendChild(modalRoot);
      
      // Criar root do React
      const root = createRoot(modalRoot);
      
      // Estado para controlar o progresso
      let progressoAtual = 0;
      let mensagemAtual = 'Atualizando';
      
      // Função para atualizar o estado do modal
      const atualizarModal = (progresso: number, mensagem: string = 'Atualizando') => {
        progressoAtual = progresso;
        mensagemAtual = mensagem;
        
        // Usar JSX diretamente
        root.render(
          <AvisoAtualizacao 
            isOpen={true} 
            progresso={progresso} 
            mensagem={mensagem} 
          />
        );
      };
      
      // Função para remover o modal
      const removerModal = () => {
        try {
          // Renderizar um último frame antes de desmontar
          root.render(
            <AvisoAtualizacao 
              isOpen={false} 
              progresso={progressoAtual} 
              mensagem={mensagemAtual} 
            />
          );
          
          // Dar tempo para a animação de saída
          setTimeout(() => {
            root.unmount();
            if (modalRoot.parentNode) {
              modalRoot.parentNode.removeChild(modalRoot);
            }
          }, 300);
        } catch (error) {
          console.error('[atualizarFontes] Erro ao remover modal:', error);
          // Tentar remover diretamente em caso de erro
          if (modalRoot.parentNode) {
            modalRoot.parentNode.removeChild(modalRoot);
          }
        }
      };

      try {
        // Importar supabase para baixar as fontes
        const { supabase } = await import('../lib/supabase');
        
        // Abre o cache para armazenar as fontes
        if (!('caches' in window)) {
          console.error('[atualizarFontes] API Cache não disponível neste navegador');
          return false;
        }
        
        // Abre o cache de fontes
        const fontCache = await caches.open(CACHE_NAME);
        let fontesAtualizadas = 0;
        let tentativasFalhas = 0;
        
        // Bucket e caminho onde estão as fontes no Supabase
        const bucket = 'assets';
        const path = 'fonts';
        
        // Atualizar status inicial no modal
        atualizarModal(0);
        
        /**
         * Processa cada fonte pendente
         * Baixa do storage, verifica integridade e adiciona ao cache
         */
        for (let i = 0; i < fontesPendentes.length; i++) {
          const fonte = fontesPendentes[i];
          try {
            // Atualizar progresso no modal considerando o progresso real
            const percentual = Math.round(((i + 1) / fontesPendentes.length) * 100);
            atualizarModal(percentual);
            //console.log(`[atualizarFontes] Progresso: ${percentual}% (${i + 1}/${fontesPendentes.length})`);
            
            // Baixa a fonte do Supabase Storage
            const { data, error: downloadError } = await supabase.storage
              .from(bucket)
              .download(`${path}/${fonte.arquivo}`);
            
            // Verifica se houve erro no download
            if (downloadError) {
              console.error(`[atualizarFontes] Erro ao baixar ${fonte.arquivo}:`, downloadError);
              tentativasFalhas++;
              continue;
            }
            
            // Verifica se o arquivo foi encontrado
            if (!data) {
              console.error(`[atualizarFontes] Arquivo ${fonte.arquivo} não encontrado no storage`);
              tentativasFalhas++;
              continue;
            }
            
            // Verificar se o arquivo foi baixado corretamente
            if (data.size === 0) {
              console.error(`[atualizarFontes] Arquivo ${fonte.arquivo} baixado com tamanho zero`);
              tentativasFalhas++;
              continue;
            }
            
            // Adiciona a fonte ao cache com a data atualizada e metadados
            await fontCache.put(
              `/fonts/${fonte.arquivo}`,
              new Response(data, {
                headers: {
                  'Content-Type': 'font/ttf',
                  'date': new Date().toUTCString(),
                  // Adicionar a data do banco como metadado para facilitar verificação
                  'data-db': fonte.data_atualizacao
                }
              })
            );
            
            // Verificar se a fonte foi adicionada corretamente ao cache
            const cacheVerify = await fontCache.match(`/fonts/${fonte.arquivo}`);
            if (!cacheVerify) {
              console.error(`[atualizarFontes] Falha ao verificar fonte ${fonte.arquivo} no cache após adicionar`);
              tentativasFalhas++;
              continue;
            }
            
            fontesAtualizadas++;
          } catch (fonteError) {
            console.error(`[atualizarFontes] Erro ao processar fonte ${fonte.arquivo}:`, fonteError);
            tentativasFalhas++;
            // Continua para a próxima fonte em caso de erro
          }
        }
        
        /**
         * Atualiza o arquivo fontes.json se pelo menos uma fonte foi atualizada
         * Este arquivo é usado para rastrear todas as fontes no cache
         */
        if (fontesAtualizadas > 0) {
          try {
            atualizarModal(95, 'Finalizando atualização...');
            
            // Forçar a recriação do arquivo fontes.json para refletir as mudanças
            // Primeiro, remova o arquivo existente se existir
            try {
              await fontCache.delete(`/${FONTS_JSON}`);
            } catch (deleteError) {
              // Continua mesmo se não conseguir excluir
            }
            
            // Agora recria o arquivo com as fontes atualizadas
            await procurarFontesJson();
            
            // Verificar se ainda há fontes pendentes após a atualização
            atualizarModal(98, 'Verificando atualizações...');
            const verificacaoFinal = await verificarPendentesAposAtualizacao();
            
            // Atualiza o progresso para 100%
            atualizarModal(100, 'Atualização concluída!');
            
            // Adicionar pequeno delay antes de fechar o modal para o usuário ver a mensagem
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Remove o modal ao finalizar
            removerModal();
            
            // Limpar o cache de fontes depois que a atualização foi concluída
            // para garantir que a próxima verificação obtenha os dados mais recentes
            limparCacheFontesDB();
            
            // IMPORTANTE: Limpar o cache global de fontes para forçar o recarregamento das fontes atualizadas
            // Isso evita que o usuário precise pressionar Ctrl+F5 para ver as fontes atualizadas
            if (options.clearFontCache) {
              options.clearFontCache();
            }
            
            // Retorna resultado baseado na verificação final
            if (verificacaoFinal.fontesPendentes === 0) {
              // Redireciona para a página de login após atualização bem-sucedida
              window.location.href = '/login';
              return true;
            } else {
              // Retorna true se pelo menos alguma fonte foi atualizada
              if (fontesAtualizadas > 0) {
                // Redireciona para a página de login se pelo menos uma fonte foi atualizada
                window.location.href = '/login';
              }
              return fontesAtualizadas > 0;
            }
          } catch (jsonError) {
            console.error('[atualizarFontes] Erro ao atualizar fontes.json:', jsonError);
            // Mesmo se falhar a atualização do JSON, algumas fontes podem ter sido baixadas
            return fontesAtualizadas > 0;
          }
        }

        // Nenhuma fonte foi atualizada (todas as tentativas falharam).
        // O modal precisa ser removido aqui também, senão fica travado sobre a tela.
        console.error(`[atualizarFontes] Nenhuma fonte atualizada (${tentativasFalhas} falha(s))`);
        atualizarModal(100, 'Erro na atualização');
        await new Promise(resolve => setTimeout(resolve, 2000));
        removerModal();

        return false;
      } catch (error) {
        // Tratamento de erro no processo de atualização
        console.error('[atualizarFontes] Erro fatal:', error);
        
        // Atualizar modal para mostrar erro
        atualizarModal(100, 'Erro na atualização');
        
        // Adicionar pequeno delay antes de fechar o modal para o usuário ler a mensagem de erro
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Remove o modal mesmo em caso de erro
        removerModal();
        
        return false;
      }
    } catch (error) {
      // Tratamento de erro externo (se o modal falhar na criação, por exemplo)
      console.error('[atualizarFontes] Erro fatal:', error);
      return false;
    }
  };

  /**
   * Verifica se ainda há fontes pendentes após uma atualização
   * Útil para confirmar se o processo de atualização foi bem sucedido
   * @returns Objeto com número de fontes pendentes e detalhes da comparação
   */
  const verificarPendentesAposAtualizacao = async (): Promise<{fontesPendentes: number, detalhes: ResultadoComparacao | null}> => {
    try {
      // Forçar uma recarga do arquivo fontes.json para garantir dados mais recentes
      if ('caches' in window) {
        const fontCache = await caches.open(CACHE_NAME);
        await fontCache.delete(`/${FONTS_JSON}`);
      }
      
      // Limpar o cache de fontes para forçar uma nova consulta
      limparCacheFontesDB();
      
      // Realizar nova comparação para verificar o estado atual
      const resultadoComparacao = await compararFontes();
      if (!resultadoComparacao) {
        return { fontesPendentes: 0, detalhes: null };
      }
      
      // Calcula o total de fontes ainda pendentes
      const pendentes = resultadoComparacao.fontesParaAtualizar.length + resultadoComparacao.fontesParaBaixar.length;
      
      return { 
        fontesPendentes: pendentes,
        detalhes: resultadoComparacao
      };
    } catch (error) {
      console.error('[verificarPendentesAposAtualizacao] Erro:', error);
      return { fontesPendentes: 0, detalhes: null };
    }
  };
  /**
   * Retorna as funções públicas do hook
   * Estas funções podem ser usadas por componentes que importarem o hook
   */
  return {
    procurarFontesJson,
    buscarFontesDatabase,
    compararFontes,
    gerarRelatorioAtualizacao,
    atualizarFontes,
    verificarPendentesAposAtualizacao,
  };
}
